import { ReplyWithProperties } from '../services/getReplyWithProperties';
import { DatapointStatus, logUsage, PerformanceDatapoint } from 'owa-analytics';
import { makeGetRequest } from 'owa-ows-gateway';
import { trace } from 'owa-trace';
import {
    AttachmentSuggestions,
    ReplyWithEndpointType,
    ReplyWithAttachmentSuggestion,
} from 'owa-mail-store';

const SEARCH_ENDPOINT = '/ows/search/api/v1/';

interface ServerResponse {
    Groups: {
        Suggestions: ReplyWithAttachmentSuggestion[];
    }[];
}

/**
 * Returns an object that maps sentences in the message body to 3S response data for attachment suggestions
 * pertaining to that sentence.
 */
export default async function getReplyWithSuggestions(
    properties: ReplyWithProperties
): Promise<AttachmentSuggestions | null> {
    try {
        const sentenceToRequestUrlMap = getSentenceToRequestUrlMap(properties);
        const sentenceTo3SResponseMap = {};
        const sentences: string[] = Object.keys(sentenceToRequestUrlMap);

        if (sentences.length > 0) {
            // Log that the message being responded to contains a ReplyWith query
            logUsage('ReplyWithQueryExecuted');

            // Call 3S for every sentence in the message body that is associated with attachment suggestions.
            await Promise.all(
                sentences.map(
                    async sentence =>
                        (sentenceTo3SResponseMap[sentence] = await executeAndLog3SQuery(
                            sentenceToRequestUrlMap[sentence]
                        ))
                )
            );

            return getSentenceToAttachmentSuggestionsMap(sentenceTo3SResponseMap);
        }
    } catch (e) {
        trace.info('Failed to parse property, error: ' + e.message);
    }

    return null;
}

async function executeAndLog3SQuery(requestUrl: string): Promise<any> {
    let response = null;
    let numResults: number = 0;
    let errorMessage: string = '';
    let queryResponseDatapoint: PerformanceDatapoint = null;

    try {
        queryResponseDatapoint = new PerformanceDatapoint('QueryResponseInformation');
        queryResponseDatapoint.addCustomData({ endpoint: ReplyWithEndpointType.Contextual });

        response = await makeGetRequest(requestUrl);
        queryResponseDatapoint.addCheckmark('Response');

        if (!response || response.length == 0) {
            errorMessage = 'No response payload';
        }
    } catch (e) {
        errorMessage = e.message;
    } finally {
        numResults = safelyGetNumberOfResults(response);

        queryResponseDatapoint.addCustomData({ numResults: numResults });

        if (errorMessage) {
            // TODO: Change to DatapointStatus.RequestNotComplete?
            // Error may not be on server end.
            queryResponseDatapoint.endWithError(
                DatapointStatus.ServerError,
                new Error(errorMessage)
            );
        } else {
            queryResponseDatapoint.end();
        }

        return response;
    }
}

/**
 * Returns an array of sentences that we shouldn't make calls to 3S for, as 3S
 * will not return any suggestions for them.
 */
function getSentencesToFilterOut(properties: ReplyWithProperties): string[] {
    const sentencesToFilterOut: string[] = [];

    if (properties.ReplyWithQueryCoverage != null) {
        const replyWithQueryCoverageData = JSON.parse(properties.ReplyWithQueryCoverage.Value)[0];
        const coverageCheckResults = replyWithQueryCoverageData.coverageCheckResults;
        const sentences = Object.keys(coverageCheckResults);

        for (let i = 0; i < sentences.length; i++) {
            const sentence = sentences[i];
            if (!coverageCheckResults[sentence]) {
                sentencesToFilterOut.push(sentence);
            }
        }
    }

    return sentencesToFilterOut;
}

/**
 * Returns an object mapping a sentence in the message body to a 3S call for attachment suggestions.
 */
function getSentenceToRequestUrlMap(
    properties: ReplyWithProperties
): { [sentence: string]: string } {
    const sentenceToRequestUrlMap = {};
    const sentencesToFilterOut: string[] = getSentencesToFilterOut(properties);

    const substrateSearchSuggestionsData = JSON.parse(
        properties.SubstrateSearchSuggestions.Value
    )[0];
    const requests = substrateSearchSuggestionsData.requests;

    requests.forEach(request => {
        const currentSentenceText = request.InputText;
        if (!sentencesToFilterOut.includes(currentSentenceText)) {
            const url = build3SQueryUrl(request.Endpoint, request.UrlParameters);
            sentenceToRequestUrlMap[currentSentenceText] = url;
        }
    });

    return sentenceToRequestUrlMap;
}

/**
 * Creates a 3S url for a sentence associated with attachment suggestions.
 */
function build3SQueryUrl(
    substrateEndpoint: string,
    urlParams: { Key: string; Value: string }[]
): string {
    let url: string = SEARCH_ENDPOINT + substrateEndpoint;
    let key: string;
    let value: string;

    for (let i = 0; i < urlParams.length; i++) {
        key = urlParams[i].Key;
        value = urlParams[i].Value;

        url += i == 0 ? '?' : '&';
        url += key + '=' + value;
    }

    return url;
}

/**
 * Safely returns the number of results from a 3S suggestions service call.
 * Used for logging.
 */
function safelyGetNumberOfResults(response: ServerResponse): number {
    try {
        return response.Groups[0].Suggestions.length;
    } catch {
        return 0;
    }
}

/**
 * Returns an object that maps sentences in the message body to sets of attachment suggestions.
 */
function getSentenceToAttachmentSuggestionsMap(sentenceTo3SResponseMap: {
    [sentence: string]: ServerResponse;
}): AttachmentSuggestions {
    try {
        const output: AttachmentSuggestions = {};
        const sentences: string[] = Object.keys(sentenceTo3SResponseMap);

        sentences.forEach(sentence => {
            output[sentence] = sentenceTo3SResponseMap[sentence].Groups[0].Suggestions;

            // Mark each suggestion that it came from ReplyWith.
            for (let i = 0; i < output[sentence].length; i++) {
                output[sentence][i].Endpoint = ReplyWithEndpointType.Contextual;
            }
        });

        return output;
    } catch (e) {
        trace.info('getSentenceToAttachmentSuggestionsMap: Failed to execute, error: ' + e.message);
        return null;
    }
}
